import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { apiFetch } from '../api';

/**
 * DataContext
 *
 * Centralized data caching and request deduplication for the LRBrain app.
 * Eliminates duplicate API calls and provides TTL-based caching.
 *
 * Features:
 * - Request deduplication: Multiple components requesting same data get same promise
 * - TTL-based caching: Data expires after configurable time
 * - Stale-while-revalidate: Return cached data immediately, refresh in background
 * - Pre-fetching: Load data before it's needed
 */

const DataContext = createContext(null);

// Cache TTL configuration (in milliseconds)
const CACHE_TTL = {
  students: 5 * 60 * 1000,       // 5 minutes
  plannerData: 2 * 60 * 1000,    // 2 minutes
  studentContext: 3 * 60 * 1000, // 3 minutes
  sessions: 2 * 60 * 1000,       // 2 minutes
  plannerSessions: 2 * 60 * 1000 // 2 minutes
};

// Stale threshold (when to trigger background refresh)
const STALE_THRESHOLD = 0.8; // Refresh when 80% of TTL has passed

export const DataProvider = ({ children }) => {
  // Cache storage: { [key]: { data, timestamp, error? } }
  const [cache, setCache] = useState({});

  // Ref to access cache in callbacks without recreating them on every cache update
  const cacheRef = useRef(cache);
  useEffect(() => {
    cacheRef.current = cache;
  }, [cache]);

  // Track in-flight requests to prevent duplicates
  const pendingRequests = useRef({});

  // Loading states per resource type
  const [loading, setLoading] = useState({
    students: false,
    plannerData: false,
    studentContext: false,
    sessions: false
  });

  /**
   * Check if cached entry is still valid
   */
  const isCacheValid = useCallback((key, ttl) => {
    const entry = cacheRef.current[key];
    if (!entry) return false;
    // Don't retry permanent errors (like 403 permission denied)
    if (entry.error && entry.permanent) return true;
    if (entry.error) return false;
    return Date.now() - entry.timestamp < ttl;
  }, []);

  /**
   * Check if cache is stale (valid but approaching expiration)
   */
  const isCacheStale = useCallback((key, ttl) => {
    const entry = cacheRef.current[key];
    if (!entry) return true;
    const age = Date.now() - entry.timestamp;
    return age > ttl * STALE_THRESHOLD;
  }, []);

  /**
   * Core fetch function with deduplication and caching
   */
  const fetchWithCache = useCallback(async (key, fetcher, ttl, options = {}) => {
    const { forceRefresh = false, background = false } = options;

    // Return cached data if valid and not forcing refresh
    if (!forceRefresh && isCacheValid(key, ttl)) {
      // Trigger background refresh if stale
      if (isCacheStale(key, ttl) && !pendingRequests.current[`bg_${key}`]) {
        fetchWithCache(key, fetcher, ttl, { background: true, forceRefresh: true });
      }
      return cacheRef.current[key].data;
    }

    // Check for in-flight request
    const pendingKey = background ? `bg_${key}` : key;
    if (pendingRequests.current[pendingKey]) {
      return pendingRequests.current[pendingKey];
    }

    // Create new request
    const requestPromise = (async () => {
      if (!background) {
        setLoading(prev => ({ ...prev, [key.split(':')[0]]: true }));
      }

      try {
        const data = await fetcher();

        setCache(prev => ({
          ...prev,
          [key]: { data, timestamp: Date.now() }
        }));

        return data;
      } catch (error) {
        // Check if this is a permission error (403) - don't retry these
        const is403 = error.status === 403 || error.message?.includes('403');

        // Cache errors (mark 403s as permanent to prevent retry loops)
        if (!background || is403) {
          setCache(prev => ({
            ...prev,
            [key]: {
              data: null,
              timestamp: Date.now(),
              error: error.message,
              permanent: is403  // Don't retry permission errors
            }
          }));
        }

        // Only throw non-permission errors to prevent retry loops
        if (!is403) throw error;
      } finally {
        delete pendingRequests.current[pendingKey];
        if (!background) {
          setLoading(prev => ({ ...prev, [key.split(':')[0]]: false }));
        }
      }
    })();

    pendingRequests.current[pendingKey] = requestPromise;
    return requestPromise;
  }, [isCacheValid, isCacheStale]);

  /**
   * Get all students (cached)
   */
  const getStudents = useCallback(async (options = {}) => {
    const fetcher = async () => {
      const response = await apiFetch('/api/demo/students');
      if (!response.ok) {
        const error = new Error(`Failed to fetch students (${response.status})`);
        error.status = response.status;
        throw error;
      }
      const data = await response.json();
      return data.students || [];
    };

    return fetchWithCache('students', fetcher, CACHE_TTL.students, options);
  }, [fetchWithCache]);

  /**
   * Get planner data for a student (cached)
   */
  const getPlannerData = useCallback(async (studentUuid = null, options = {}) => {
    const key = studentUuid ? `plannerData:${studentUuid}` : 'plannerData:default';

    const fetcher = async () => {
      const url = studentUuid
        ? `/api/planner-data?student_uuid=${studentUuid}`
        : '/api/planner-data';
      const response = await apiFetch(url);
      if (!response.ok) throw new Error('Failed to fetch planner data');
      return response.json();
    };

    return fetchWithCache(key, fetcher, CACHE_TTL.plannerData, options);
  }, [fetchWithCache]);

  /**
   * Get student context (profile + transcripts) - cached
   */
  const getStudentContext = useCallback(async (uuid, options = {}) => {
    if (!uuid) return null;

    const key = `studentContext:${uuid}`;

    const fetcher = async () => {
      const response = await apiFetch(`/api/students/${uuid}/context`);
      if (!response.ok) throw new Error('Failed to fetch student context');
      return response.json();
    };

    return fetchWithCache(key, fetcher, CACHE_TTL.studentContext, options);
  }, [fetchWithCache]);

  /**
   * Get planner sessions with filters (cached by filter combination)
   */
  const getPlannerSessions = useCallback(async (filters = {}, options = {}) => {
    const { query = '', focus = '' } = filters;
    const key = `plannerSessions:${query}:${focus}`;

    const fetcher = async () => {
      const params = new URLSearchParams();
      if (query) params.set('query', query);
      if (focus) params.set('focus', focus);

      const url = `/api/planner-sessions${params.toString() ? '?' + params.toString() : ''}`;
      const response = await apiFetch(url);
      if (!response.ok) throw new Error('Failed to fetch planner sessions');
      return response.json();
    };

    return fetchWithCache(key, fetcher, CACHE_TTL.plannerSessions, options);
  }, [fetchWithCache]);

  /**
   * Pre-fetch multiple resources in parallel
   */
  const prefetch = useCallback(async (resources) => {
    const fetchPromises = resources.map(resource => {
      switch (resource) {
        case 'students':
          return getStudents().catch(() => null);
        case 'plannerData':
          return getPlannerData().catch(() => null);
        default:
          return Promise.resolve(null);
      }
    });

    await Promise.all(fetchPromises);
  }, [getStudents, getPlannerData]);

  /**
   * Invalidate specific cache entries
   */
  const invalidate = useCallback((pattern) => {
    setCache(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(key => {
        if (key === pattern || key.startsWith(`${pattern}:`)) {
          delete next[key];
        }
      });
      return next;
    });
  }, []);

  /**
   * Invalidate all cache entries
   */
  const invalidateAll = useCallback(() => {
    setCache({});
  }, []);

  /**
   * Get cached value directly (without fetching)
   */
  const getCached = useCallback((key) => {
    const entry = cacheRef.current[key];
    return entry?.data || null;
  }, []);

  // Expose students directly for components that just need the cached list
  const students = cache.students?.data || [];
  const studentsLoading = loading.students;
  const studentsError = cache.students?.error || null;

  const value = {
    // Data fetching methods
    getStudents,
    getPlannerData,
    getStudentContext,
    getPlannerSessions,

    // Cache management
    prefetch,
    invalidate,
    invalidateAll,
    getCached,

    // Direct access to commonly used data
    students,
    studentsLoading,
    studentsError,

    // Loading states
    loading
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};

/**
 * Hook to access DataContext
 */
export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export default DataContext;
